/**
 * 3:4 crop window. Drag the photo to reframe; zoom with the slider.
 */
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { Button } from "@ui/primitives";
import { fontSize, space } from "@design/index";
import {
  COVER_OUT_HEIGHT, COVER_OUT_WIDTH,
  centeredCoverOffset, clampCoverOffset, coverSourceRect, minCoverScale,
} from "./coverPhoto";

export function CoverCrop({
  src, width, height, onCancel, onConfirm, busy,
}: {
  src: string;
  width: number;
  height: number;
  onCancel: () => void;
  onConfirm: (file: File) => void;
  busy?: boolean;
}) {
  const min = minCoverScale(width, height);
  const [scale, setScale] = useState(min);
  const [pos, setPos] = useState(() => centeredCoverOffset(width, height, min));
  const [ready, setReady] = useState(false);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const next = minCoverScale(width, height);
    setScale(next);
    setPos(centeredCoverOffset(width, height, next));
    setReady(Boolean(imgRef.current?.complete));
  }, [src, width, height]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  function applyScale(next: number) {
    const s = Math.max(min, Math.min(min * 4, next));
    setScale(s);
    setPos((p) => clampCoverOffset(width, height, s, p.x, p.y));
  }

  function onPointerDown(e: PointerEvent) {
    if (busy) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, ox: pos.x, oy: pos.y };
  }
  function onPointerMove(e: PointerEvent) {
    if (!drag.current) return;
    setPos(clampCoverOffset(
      width, height, scale,
      drag.current.ox + (e.clientX - drag.current.x),
      drag.current.oy + (e.clientY - drag.current.y),
    ));
  }
  function onPointerUp() { drag.current = null; }

  async function confirm() {
    const img = imgRef.current;
    if (!img || !img.complete) return;
    const { sx, sy, sw, sh } = coverSourceRect(width, height, scale, pos.x, pos.y);
    const canvas = document.createElement("canvas");
    canvas.width = COVER_OUT_WIDTH;
    canvas.height = COVER_OUT_HEIGHT;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, COVER_OUT_WIDTH, COVER_OUT_HEIGHT);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.92);
    });
    if (!blob) return;
    onConfirm(new File([blob], "cover.jpg", { type: "image/jpeg" }));
  }

  return (
    <div className="cm-cover-crop" role="dialog" aria-modal="true" aria-labelledby="cover-crop-title">
      <div className="cm-cover-crop-card">
        <h2 id="cover-crop-title" style={{ margin: 0, fontSize: fontSize.h3, fontWeight: 700 }}>
          Crop to 3:4
        </h2>
        <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.55 }}>
          Drag the photo to frame the cover. Zoom if you need a tighter crop.
        </p>
        <div
          className="cm-cover-crop-frame"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            draggable={false}
            onLoad={() => setReady(true)}
            style={{
              width: width * scale,
              height: height * scale,
              transform: `translate(${pos.x}px, ${pos.y}px)`,
              maxWidth: "none",
              display: "block",
              pointerEvents: "none",
            }}
          />
        </div>
        <label className="cm-cover-crop-zoom">
          <span>Zoom</span>
          <input
            type="range" min={min} max={min * 4} step={min / 40}
            value={scale} disabled={busy}
            onChange={(e) => applyScale(Number(e.currentTarget.value))}
          />
        </label>
        <div style={{ display: "flex", gap: space(2), flexWrap: "wrap" }}>
          <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
          <Button type="button" variant="primary" disabled={busy || !ready} onClick={() => void confirm()}>
            {busy ? "Uploading…" : "Use this crop"}
          </Button>
        </div>
      </div>
    </div>
  );
}
